import type { VehicleCategory } from '../types';
import { getVehicleAvatar, getTowVehicleAvatar } from './vehicleAvatars';

// ── Vehicle colors per category (matches VehicleMarkerSvg.tsx) ──────

const VEHICLE_COLORS: Record<string, string> = {
  motorhome: '#3b82f6',
  campervan: '#a855f7',
  caravan: '#14b8a6',
  camper_trailer: '#f43f5e',
  rooftent: '#f97316',
  offroad: '#84cc16',
  car: '#64748b',
};

// ── SVG path strings per category (extracted from VehicleMarkerSvg.tsx StandalonePaths) ──

const STANDALONE_SVG: Record<string, string> = {
  motorhome: [
    '<rect x="10" y="6" width="20" height="28" rx="4" fill="FILL"/>',
    '<path d="M12 6 Q20 0 28 6" fill="FILL"/>',
    '<rect x="13" y="8" width="14" height="4" rx="1.5" fill="rgba(255,255,255,0.4)"/>',
    '<rect x="13" y="15" width="14" height="1" rx="0.5" fill="rgba(255,255,255,0.15)"/>',
    '<rect x="12" y="32" width="16" height="2" rx="1" fill="rgba(0,0,0,0.3)"/>',
  ].join(''),
  campervan: [
    '<rect x="11" y="8" width="18" height="24" rx="5" fill="FILL"/>',
    '<path d="M13 8 Q20 2 27 8" fill="FILL"/>',
    '<rect x="14" y="10" width="12" height="3" rx="1.5" fill="rgba(255,255,255,0.4)"/>',
    '<rect x="13" y="16" width="14" height="1" rx="0.5" fill="rgba(255,255,255,0.15)"/>',
  ].join(''),
  caravan: [
    '<rect x="11" y="10" width="18" height="20" rx="3" fill="FILL"/>',
    '<rect x="14" y="12" width="12" height="4" rx="1.5" fill="rgba(255,255,255,0.4)"/>',
    '<rect x="14" y="18" width="12" height="1" rx="0.5" fill="rgba(255,255,255,0.1)"/>',
    '<circle cx="16" cy="30" r="2" fill="rgba(0,0,0,0.3)"/>',
    '<circle cx="24" cy="30" r="2" fill="rgba(0,0,0,0.3)"/>',
    '<rect x="12" y="30" width="16" height="1.5" rx="0.5" fill="rgba(0,0,0,0.2)"/>',
  ].join(''),
  camper_trailer: [
    '<rect x="12" y="14" width="16" height="14" rx="2" fill="FILL"/>',
    '<path d="M13 14 L20 8 L27 14" fill="FILL" opacity="0.8"/>',
    '<circle cx="16" cy="28" r="2" fill="rgba(0,0,0,0.3)"/>',
    '<circle cx="24" cy="28" r="2" fill="rgba(0,0,0,0.3)"/>',
  ].join(''),
  rooftent: [
    '<rect x="12" y="16" width="16" height="16" rx="3" fill="FILL"/>',
    '<path d="M14 16 Q20 10 26 16" fill="FILL"/>',
    '<rect x="15" y="12" width="10" height="3" rx="1" fill="rgba(255,255,255,0.4)"/>',
    '<rect x="13" y="6" width="14" height="6" rx="1.5" fill="FILL" opacity="0.7"/>',
    '<rect x="14" y="7" width="12" height="4" rx="1" fill="rgba(255,255,255,0.15)"/>',
  ].join(''),
  offroad: [
    '<rect x="10" y="8" width="20" height="24" rx="3" fill="FILL"/>',
    '<rect x="12" y="6.5" width="16" height="3" rx="1" fill="rgba(60,60,60,0.5)"/>', // Bull bar simplified
    '<rect x="13" y="10" width="14" height="4" rx="1.5" fill="rgba(255,255,255,0.4)"/>', // Windshield
    '<rect x="12" y="18" width="16" height="7" rx="1" fill="rgba(255,255,255,0.1)"/>', // Roof rack
    '<rect x="11" y="30" width="18" height="2" rx="1" fill="rgba(0,0,0,0.3)"/>',
  ].join(''),
  car: [
    '<rect x="11" y="10" width="18" height="20" rx="4" fill="FILL"/>',
    '<rect x="13" y="12" width="14" height="5" rx="1.5" fill="rgba(255,255,255,0.4)"/>', // Windshield
    '<rect x="12" y="28" width="16" height="2" rx="0.5" fill="rgba(0,0,0,0.2)"/>',
  ].join(''),
};

// ── Standalone trailer SVG paths (3D gradient versions) ──

const STANDALONE_TRAILER_SVG: Record<string, string> = {
  caravan: [
    '<defs>',
    '<linearGradient id="st-cr-body" x1="0" y1="0" x2="1" y2="0">',
    '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
    '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
    '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
    '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
    '</linearGradient>',
    '<linearGradient id="st-cr-glass" x1="0" y1="0" x2="1" y2="1">',
    '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.85"/>',
    '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.65"/>',
    '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.8"/>',
    '</linearGradient>',
    '<radialGradient id="st-cr-wheel" cx="0.4" cy="0.35">',
    '<stop offset="0%" stop-color="#555"/>',
    '<stop offset="50%" stop-color="#222"/>',
    '<stop offset="100%" stop-color="#111"/>',
    '</radialGradient>',
    '</defs>',
    // Hitch arm
    '<rect x="19" y="1" width="2.5" height="6" rx="0.6" fill="#4b5563"/>',
    '<circle cx="20.25" cy="1.5" r="1.6" fill="#6b7280"/>',
    '<circle cx="20.25" cy="1.5" r="0.7" fill="#9ca3af"/>',
    // Main body
    '<rect x="10" y="7" width="20" height="21" rx="3" fill="FILL"/>',
    '<rect x="10" y="7" width="20" height="21" rx="3" fill="url(#st-cr-body)"/>',
    // Front window
    '<rect x="13" y="9" width="14" height="5" rx="1.5" fill="url(#st-cr-glass)"/>',
    // Body stripe
    '<rect x="12" y="16" width="16" height="1" rx="0.5" fill="rgba(255,255,255,0.3)"/>',
    // Side windows
    '<rect x="13" y="18" width="6" height="3" rx="1" fill="url(#st-cr-glass)" opacity="0.5"/>',
    '<rect x="21" y="18" width="6" height="3" rx="1" fill="url(#st-cr-glass)" opacity="0.4"/>',
    // Rear bumper
    '<rect x="12" y="26" width="16" height="1.5" rx="0.5" fill="rgba(0,0,0,0.3)"/>',
    // Wheels
    '<ellipse cx="13" cy="26" rx="2.2" ry="2.5" fill="url(#st-cr-wheel)"/>',
    '<ellipse cx="13" cy="26" rx="0.9" ry="1" fill="rgba(180,180,180,0.5)"/>',
    '<ellipse cx="27" cy="26" rx="2.2" ry="2.5" fill="url(#st-cr-wheel)"/>',
    '<ellipse cx="27" cy="26" rx="0.9" ry="1" fill="rgba(180,180,180,0.4)"/>',
    // Left edge highlight
    '<rect x="10" y="9" width="0.7" height="18" rx="0.35" fill="rgba(255,255,255,0.3)"/>',
  ].join(''),
  camper_trailer: [
    '<defs>',
    '<linearGradient id="st-ct-body" x1="0" y1="0" x2="1" y2="0">',
    '<stop offset="0%" stop-color="white" stop-opacity="0.25"/>',
    '<stop offset="40%" stop-color="white" stop-opacity="0"/>',
    '<stop offset="60%" stop-color="black" stop-opacity="0"/>',
    '<stop offset="100%" stop-color="black" stop-opacity="0.25"/>',
    '</linearGradient>',
    '<linearGradient id="st-ct-glass" x1="0" y1="0" x2="1" y2="1">',
    '<stop offset="0%" stop-color="#b4e0ff" stop-opacity="0.85"/>',
    '<stop offset="40%" stop-color="#7cc4f5" stop-opacity="0.65"/>',
    '<stop offset="100%" stop-color="#4a90c4" stop-opacity="0.8"/>',
    '</linearGradient>',
    '<radialGradient id="st-ct-wheel" cx="0.4" cy="0.35">',
    '<stop offset="0%" stop-color="#555"/>',
    '<stop offset="50%" stop-color="#222"/>',
    '<stop offset="100%" stop-color="#111"/>',
    '</radialGradient>',
    '</defs>',
    // Hitch arm
    '<rect x="19" y="1" width="2.5" height="6" rx="0.6" fill="#4b5563"/>',
    '<circle cx="20.25" cy="1.5" r="1.6" fill="#6b7280"/>',
    '<circle cx="20.25" cy="1.5" r="0.7" fill="#9ca3af"/>',
    // Main body
    '<rect x="11" y="10" width="18" height="15" rx="2" fill="FILL"/>',
    // A-frame roof
    '<path d="M12 10 L20 3 L28 10" fill="FILL" opacity="0.9"/>',
    '<path d="M12 10 L20 3 L20 10 Z" fill="rgba(255,255,255,0.2)"/>',
    '<path d="M20 3 L28 10 L20 10 Z" fill="rgba(0,0,0,0.15)"/>',
    // Body gradient
    '<rect x="11" y="10" width="18" height="15" rx="2" fill="url(#st-ct-body)"/>',
    // Window
    '<rect x="14" y="12" width="12" height="4" rx="1" fill="url(#st-ct-glass)" opacity="0.6"/>',
    // Body detail
    '<rect x="13" y="18" width="14" height="0.8" rx="0.4" fill="rgba(255,255,255,0.25)"/>',
    // Wheels
    '<ellipse cx="14" cy="24" rx="2" ry="2.3" fill="url(#st-ct-wheel)"/>',
    '<ellipse cx="14" cy="24" rx="0.8" ry="0.9" fill="rgba(180,180,180,0.5)"/>',
    '<ellipse cx="26" cy="24" rx="2" ry="2.3" fill="url(#st-ct-wheel)"/>',
    '<ellipse cx="26" cy="24" rx="0.8" ry="0.9" fill="rgba(180,180,180,0.4)"/>',
    // Left edge highlight
    '<rect x="11" y="11" width="0.7" height="13" rx="0.35" fill="rgba(255,255,255,0.3)"/>',
  ].join(''),
};

// ── Color helpers ───────────────────────────────────────────────────

/** Check if a hex color is "light" (luminance > 0.6) — needs dark bg for contrast */
export function isLightColor(hex: string): boolean {
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  // Relative luminance (simplified)
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.6;
}

/** Add stroke outlines to FILL-colored SVG shapes for visibility */
function addStrokeToSvg(svg: string, color: string): string {
  const strokeColor = isLightColor(color) ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)';
  // Add stroke to elements that have fill="<color>" (the main body shapes)
  return svg.replace(
    new RegExp(`fill="${color.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`, 'g'),
    `fill="${color}" stroke="${strokeColor}" stroke-width="0.5"`
  );
}

// ── Public API ──────────────────────────────────────────────────────

export interface VehicleMarkerOptions {
  category: VehicleCategory;
  /** URL to vehicle photo — if provided, renders photo instead of SVG */
  photoUrl?: string | null;
  /** @deprecated AI-generated SVG — kept for backwards compat, ignored */
  aiAvatarSvg?: string | null;
  /** GPS freshness border color (green/red) */
  borderColor: string;
  /** GPS freshness pulse ring color (translucent green/red) */
  pulseColor: string;
  /** Marker size in px (default 44) */
  size?: number;
  /** User-chosen custom color — overrides category default */
  customColor?: string | null;
}

/** Simple cache to avoid rebuilding identical HTML strings */
const _cache = new Map<string, string>();

/**
 * Generate raw HTML string for a vehicle GPS marker.
 * Priority: photo > AI-generated SVG > generic category SVG.
 * Designed for use in L.divIcon.html and maplibregl.Marker DOM elements.
 */
export function getVehicleMarkerHtml(opts: VehicleMarkerOptions): string {
  const size = opts.size || 44;
  const hasPhoto = !!opts.photoUrl;
  const cc = opts.customColor || '';
  const cacheKey = `${opts.category}_${hasPhoto ? opts.photoUrl : 'avatar'}_${opts.borderColor}_${size}_${cc}`;
  const cached = _cache.get(cacheKey);
  if (cached) return cached;

  const vehicleColor = opts.customColor || VEHICLE_COLORS[opts.category] || VEHICLE_COLORS.motorhome;
  const innerSize = size - 8;
  const svgSize = size - 18;

  let innerContent: string;
  if (hasPhoto) {
    // Photo avatar — round cropped vehicle image
    innerContent = `<img src="${opts.photoUrl}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%" crossorigin="anonymous"/>`;
  } else {
    // 3D category avatar with user's color
    let svgPaths = getVehicleAvatar(opts.category).replace(/FILL/g, vehicleColor);
    svgPaths = addStrokeToSvg(svgPaths, vehicleColor);
    innerContent = `<svg viewBox="0 0 40 40" width="${svgSize + 4}" height="${svgSize + 4}" style="flex-shrink:0">${svgPaths}</svg>`;
  }

  // Use dark background when vehicle color is light (e.g. white, beige, silver)
  const bgColor = hasPhoto ? '#1e293b' : isLightColor(vehicleColor) ? '#1e293b' : 'white';
  const html = `<div style="position:relative;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center">
<div style="position:absolute;inset:0;border-radius:50%;background:${opts.pulseColor};animation:vmarker-pulse 2s ease-in-out infinite"></div>
<div style="position:relative;width:${innerSize}px;height:${innerSize}px;border-radius:50%;background:${bgColor};border:3px solid ${opts.borderColor};box-shadow:0 2px 8px rgba(0,0,0,0.4),0 0 0 1px rgba(255,255,255,0.1);display:flex;align-items:center;justify-content:center;overflow:hidden">
${innerContent}
</div>
</div>`;

  _cache.set(cacheKey, html);
  return html;
}

/** CSS keyframes for vehicle marker pulse — inject once into document */
let _styleInjected = false;
export function ensureVehicleMarkerStyles(): void {
  if (_styleInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = [
    `@keyframes vmarker-pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.3);opacity:0.5}}`,
    `@keyframes vmarker-glow{0%,100%{filter:drop-shadow(0 0 4px var(--glow-color)) drop-shadow(0 0 8px var(--glow-color))}50%{filter:drop-shadow(0 0 8px var(--glow-color)) drop-shadow(0 0 16px var(--glow-color))}}`,
  ].join('\n');
  document.head.appendChild(style);
  _styleInjected = true;
}

/** Get the category color for preview purposes */
export function getVehicleCategoryColor(category: VehicleCategory): string {
  return VEHICLE_COLORS[category] || VEHICLE_COLORS.motorhome;
}

// ── 3D driving marker (no circular frame — vehicle "drives" on the map) ──

export interface DrivingMarkerOptions {
  category: VehicleCategory;
  /** User-chosen custom color — overrides category default */
  customColor?: string | null;
  /** Marker size in px (default 48) */
  size?: number;
  /** GPS freshness indicator dot color (green/red), null = no dot */
  freshnessColor?: string | null;
}

/**
 * Generate HTML for a 3D vehicle marker that sits directly on the map.
 * No circular frame — just the vehicle SVG with drop shadow and a pulsing GPS glow.
 * Green glow = fresh GPS, red glow = stale GPS.
 */
export function getVehicleDrivingMarkerHtml(opts: DrivingMarkerOptions): string {
  ensureVehicleMarkerStyles();
  const size = opts.size || 48;
  const cc = opts.customColor || '';
  const cacheKey = `driving3_${opts.category}_${size}_${cc}_${opts.freshnessColor || ''}`;
  const cached = _cache.get(cacheKey);
  if (cached) return cached;

  const vehicleColor = opts.customColor || VEHICLE_COLORS[opts.category] || VEHICLE_COLORS.motorhome;
  let svgPaths = getVehicleAvatar(opts.category).replace(/FILL/g, vehicleColor);
  svgPaths = addStrokeToSvg(svgPaths, vehicleColor);

  // Pulsing glow follows the vehicle shape via drop-shadow filter animation
  const glowColor = opts.freshnessColor || '#22c55e';

  const html = `<div style="width:${size}px;height:${size}px;--glow-color:${glowColor};animation:vmarker-glow 2s ease-in-out infinite">
<svg viewBox="0 0 40 40" width="${size}" height="${size}">${svgPaths}</svg></div>`;

  _cache.set(cacheKey, html);
  return html;
}

// ── Articulated vogntog markers (tow + trailer as separate elements) ──

/** Generate HTML for the tow vehicle (SUV) marker — used as a separate marker */
export function getTowVehicleMarkerHtml(size = 36, freshnessColor?: string): string {
  ensureVehicleMarkerStyles();
  const cacheKey = `tow3d_${size}_${freshnessColor || ''}`;
  const cached = _cache.get(cacheKey);
  if (cached) return cached;
  const towSvg = getTowVehicleAvatar();
  const h = Math.round(size * 0.65);
  const glowStyle = freshnessColor
    ? `--glow-color:${freshnessColor};animation:vmarker-glow 2s ease-in-out infinite`
    : `filter:drop-shadow(0 3px 5px rgba(0,0,0,0.4))`;
  const html = `<div style="width:${size}px;height:${h}px;${glowStyle}">
<svg viewBox="0 0 40 24" width="${size}" height="${h}">${towSvg}</svg></div>`;
  _cache.set(cacheKey, html);
  return html;
}

/** Generate HTML for a standalone trailer marker — used as a separate marker */
export function getTrailerMarkerHtml(category: VehicleCategory, size = 32, customColor?: string | null): string {
  const color = customColor || VEHICLE_COLORS[category] || VEHICLE_COLORS.caravan;
  const key = category === 'camper_trailer' ? 'camper_trailer' : 'caravan';
  const cacheKey = `trailer_${key}_${size}_${customColor || ''}`;
  const cached = _cache.get(cacheKey);
  if (cached) return cached;
  let paths = (STANDALONE_TRAILER_SVG[key] || STANDALONE_TRAILER_SVG.caravan)
    .replace(/FILL/g, color);
  paths = addStrokeToSvg(paths, color);
  const h = Math.round(size * 0.85);
  const html = `<div style="width:${size}px;height:${h}px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35))">
<svg viewBox="0 0 40 30" width="${size}" height="${h}">${paths}</svg></div>`;
  _cache.set(cacheKey, html);
  return html;
}
